import { Category } from "@/types/category";
import { PublicServiceDetail } from "@/types/service";

import { sortPromotedServicesFirst } from "./helpers";

export interface TaggedServiceGroup {
  id: string;
  title: string;
  services: PublicServiceDetail[];
}

export const OTHER_SERVICES_CATEGORY_NAME = "Other Services";

export const getLargeLayoutCategoryName = (service: PublicServiceDetail) =>
  service.category ?? OTHER_SERVICES_CATEGORY_NAME;

// Resolve the highest configured ancestor; invalid/cyclic links fall back to
// the service's own category so no services disappear from the menu.
export function getSidebarCategoryName(
  service: PublicServiceDetail,
  categories: Category[],
): string {
  const name = getLargeLayoutCategoryName(service);
  const byName = new Map(
    categories.map((category) => [category.name, category]),
  );
  const seen = new Set([name]);
  let current = name;
  let parent = byName.get(current)?.parentCategoryName;
  while (parent && byName.has(parent)) {
    if (seen.has(parent)) return name;
    seen.add(parent);
    current = parent;
    parent = byName.get(current)?.parentCategoryName;
  }
  return current;
}

export function groupServicesByCategory(
  services: PublicServiceDetail[],
  sidebarCategory: string | undefined,
  categories: Category[],
): ReturnType<typeof groupServicesByTag> {
  const groups = new Map<string, PublicServiceDetail[]>();
  const untaggedServices: PublicServiceDetail[] = [];
  for (const service of services) {
    const name = getLargeLayoutCategoryName(service);
    if (name === sidebarCategory || !service.category) {
      untaggedServices.push(service);
      continue;
    }
    const group = groups.get(name) ?? [];
    group.push(service);
    groups.set(name, group);
  }
  const order = new Map(
    categories.map((category) => [
      category.name,
      category.displayOrder ?? Infinity,
    ]),
  );
  return {
    tagGroups: Array.from(groups, ([name, items]) => ({
      id: name,
      title: name,
      services: sortPromotedServicesFirst(items),
    })).sort((a, b) => {
      const difference =
        (order.get(a.title) ?? Infinity) - (order.get(b.title) ?? Infinity);
      return (
        (Number.isNaN(difference) ? 0 : difference) ||
        a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
      );
    }),
    untaggedServices: sortPromotedServicesFirst(untaggedServices),
  };
}

export function groupServicesByTag(services: PublicServiceDetail[]): {
  tagGroups: TaggedServiceGroup[];
  untaggedServices: PublicServiceDetail[];
} {
  const groups = new Map<
    string,
    { title: string; services: PublicServiceDetail[] }
  >();
  const untaggedServices: PublicServiceDetail[] = [];

  for (const service of services) {
    if (!service.tags?.length) {
      untaggedServices.push(service);
      continue;
    }

    const tags = [...service.tags].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );

    for (const tag of tags) {
      const key = tag.id;
      if (!groups.has(key)) groups.set(key, { title: tag.name, services: [] });
      groups.get(key)!.services.push(service);
    }
  }

  const tagGroups = Array.from(groups.entries())
    .map(([id, group]) => ({
      id,
      title: group.title,
      services: sortPromotedServicesFirst(group.services),
    }))
    .sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
    );

  return {
    tagGroups,
    untaggedServices: sortPromotedServicesFirst(untaggedServices),
  };
}
